import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationProperties } from "../../../../../../hooks/useApplicationProperties"
import { digitalManagerApi } from "../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../components/FormPage"
import PropertyName from "../../../../../components/forms/PropertyName"
import PropertyValue from "../../../../../components/forms/PropertyValue"
import {
  ApplicationPropertyDetail,
  ApplicationPropertyGet,
  ApplicationPropertyPossibleValue,
  ApplicationPropertyTypeEnum,
  ApplicationPropertyValPostUserRole,
} from "../../../../../models/ApplicationProperty"
import { ApplicationRoleUserGet } from "../../../../../models/ApplicationRoleUser"

const validationSchema = z.object({
  propertyName: z.string().min(1, "Property name is required"),
  propertyValue: z.string().min(1, "Property value is required"),
  roleId: z.string(),
  userId: z.string(),
})

export function UserApplicationRolePropertyCreate() {
  const { userId, applicationId, roleId } = useParams()
  const { applicationProperties, isLoadingApplicationProperties } =
    useApplicationProperties(applicationId, false, true)
  const [
    applicationRolePermissionPropertiesIds,
    setApplicationRolePermissionPropertiesIds,
  ] = useState<ApplicationPropertyDetail[]>([])
  const navigate = useNavigate()
  const returnURL = `/users/${userId}/applications/${applicationId}/roles/${roleId}#properties`

  const [propertyType, setPropertyType] = useState<ApplicationPropertyTypeEnum>(
    ApplicationPropertyTypeEnum.String
  )
  const [propertyPossibleValue, setPropertyPossibleValue] =
    useState<ApplicationPropertyPossibleValue>({ value: "", label: "" })

  const propertyPossibleValues: { [key: string]: ApplicationPropertyPossibleValue[] } =
    useMemo(() => {
      const propertyPossibleValuesDict: {
        [key: string]: ApplicationPropertyPossibleValue[]
      } = {}
      if (applicationProperties?.length) {
        applicationProperties.forEach((property: ApplicationPropertyGet) => {
          propertyPossibleValuesDict[property.type] = property.possibleValues
        })
      }
      return propertyPossibleValuesDict
    }, [applicationProperties])

  useEffect(() => {
    if (userId && applicationId && roleId) {
      digitalManagerApi
        .get<ApplicationRoleUserGet>(
          `/users/${userId}/applications/${applicationId}/roles/${roleId}`
        )
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data?.properties.length) {
            const filteredProperties = res.data.properties.map(
              (property: ApplicationPropertyDetail) => ({
                name: property.name,
                value: property.value,
              })
            )
            setApplicationRolePermissionPropertiesIds(filteredProperties)
          }
        })
        .catch((err) => {
          if (err.response.status) {
            navigate(returnURL)
          }
        })
    }
  }, [applicationId, navigate, returnURL, roleId, userId])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
    setValue,
  } = useForm<{ propertyName: string } & ApplicationPropertyValPostUserRole>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      propertyName: "",
      propertyValue: "",
      userId,
      roleId,
    })
  }, [reset, roleId, userId])

  const onSubmit = useCallback(
    async (values: { propertyName: string } & ApplicationPropertyValPostUserRole) => {
      try {
        const response = await digitalManagerApi.post(
          `/applications/${applicationId}/properties/${values.propertyName}/values`,
          {
            propertyValue: values.propertyValue,
            userId: values.userId,
            roleId: values.roleId,
          }
        )
        if (response.status === StatusCodes.CREATED) {
          toast.success("Property added to role's permission")
          navigate(returnURL)
        }
      } catch {
        toast.error("Cannot add property to user's role")
      }
    },
    [applicationId, navigate, returnURL]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Property Name">
        <>
          <PropertyName<{ propertyName: string } & ApplicationPropertyValPostUserRole>
            control={control}
            setValue={setValue}
            applicationProperties={applicationProperties}
            setPropertyType={setPropertyType}
            setPropertyPossibleValue={setPropertyPossibleValue}
            isLoadingApplicationProperties={isLoadingApplicationProperties}
            errors={errors}
          />
          {errors.propertyName && (
            <div className="text-error text-caption">{errors.propertyName.message}</div>
          )}
        </>
      </FieldRow>
      <FieldRow label="Property Value">
        <PropertyValue<{ propertyName: string } & ApplicationPropertyValPostUserRole>
          control={control}
          setValue={setValue}
          applicationRolePermissionPropertiesIds={applicationRolePermissionPropertiesIds}
          propertyPossibleValues={propertyPossibleValues}
          propertyType={propertyType}
          propertyPossibleValue={propertyPossibleValue}
          setPropertyPossibleValue={setPropertyPossibleValue}
          errors={errors}
        />
      </FieldRow>
    </FormPage>
  )
}
